import {ErrorNotice, Loading} from './components';
import type {HermesState} from './lib/types';
import {PRODUCT_ENTRY_LABEL} from '../src/product';
import './storage-usage.css';

const megabytes = (bytes: number): string => new Intl.NumberFormat(undefined, {maximumFractionDigits: 2}).format(bytes / 1_000_000);
const percentage = new Intl.NumberFormat(undefined, {style: 'percent', maximumFractionDigits: 1});

export function StorageUsage({state, compact = false, showError = true}: {state: HermesState; compact?: boolean; showError?: boolean}) {
  const storage = state.nativeRuntime.data?.storage;
  const allocation = storage?.allocated ? {
    used: storage.usedBytes / storage.limitBytes,
    available: storage.availableBytes / storage.limitBytes,
    unavailableBytes: storage.limitBytes - storage.usedBytes - storage.availableBytes,
  } : null;
  const description = storage && allocation
    ? `${megabytes(storage.usedBytes)} MB used (${percentage.format(allocation.used)}). ${megabytes(storage.availableBytes)} MB available. ${megabytes(allocation.unavailableBytes)} MB for the filesystem and unavailable space. ${megabytes(storage.limitBytes)} MB allocated.`
    : undefined;
  return <section className={`storage-usage${compact ? ' storage-compact' : ''}`} aria-label="Private storage">
    {showError && <ErrorNotice error={state.nativeRuntime.error}/>}
    {!storage && <Loading active={state.nativeRuntime.status === 'idle' || state.nativeRuntime.status === 'loading'}/>}
    {storage && state.nativeRuntime.status !== 'error' && <>
      <div className="storage-meter-heading"><p className="eyebrow">Storage</p>
        {allocation && <span className="storage-meter-capacity" title="Allocated storage">{megabytes(storage.limitBytes)} MB</span>}</div>
      {allocation ? <>
        <div className="storage-meter" role="meter" aria-label="Storage used" aria-valuemin={0}
          aria-valuemax={storage.limitBytes} aria-valuenow={storage.usedBytes} aria-valuetext={description}
          title={description} tabIndex={0}>
          <span className="storage-meter-used" style={{width: `${allocation.used * 100}%`}} aria-hidden="true"/>
          <span className="storage-meter-available" style={{width: `${allocation.available * 100}%`}} aria-hidden="true"/>
          <span className="storage-meter-unavailable" style={{width: `${allocation.unavailableBytes / storage.limitBytes * 100}%`}} aria-hidden="true"/>
        </div>
        <div className="storage-meter-labels">
          <p><strong>{megabytes(storage.usedBytes)} MB</strong> used</p>
          <p>{megabytes(storage.availableBytes)} MB available</p>
        </div>
        {!compact && <p className="field-help">Your files, chats, and working data share this space. Learned memory uses separate storage.</p>}
      </> : <p className="field-help">Select “{PRODUCT_ENTRY_LABEL}” to prepare your private file space.</p>}
    </>}
  </section>;
}
